// Display-layer formatting only. Money is stored and passed around as integer
// pence everywhere else; it becomes a string exactly once, here.

const gbp = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
});

/** 232435 -> "£2,324.35" */
export function formatMoney(minor: number): string {
  return gbp.format(minor / 100);
}

const monthLabel = new Intl.DateTimeFormat("en-GB", {
  month: "long",
  year: "numeric",
});

/** "2026-06" -> "June 2026" */
export function formatPeriod(period: string): string {
  const [year, month] = period.split("-").map(Number);
  return monthLabel.format(new Date(Date.UTC(year, month - 1, 1)));
}

/** "12.50" or "£1,234.5" -> 1250 pence. Returns null if not a valid amount. */
export function parseMoney(input: string): number | null {
  const cleaned = input.replace(/[£,\s]/g, "");
  if (cleaned === "" || !/^\d*\.?\d{0,2}$/.test(cleaned)) return null;
  const pounds = Number(cleaned);
  if (!Number.isFinite(pounds)) return null;
  return Math.round(pounds * 100);
}

/** Today's date as ISO YYYY-MM-DD (local time). */
export function todayISO(): string {
  const d = new Date();
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

const dayMonth = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short" });

/** "2026-06-27" -> "27 Jun" */
export function formatDayMonth(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return dayMonth.format(new Date(Date.UTC(y, m - 1, d)));
}

const dayMonthYear = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "2-digit" });

/** "2026-03-15" -> "15 Mar 26" */
export function formatDayMonthYear(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return dayMonthYear.format(new Date(Date.UTC(y, m - 1, d)));
}

const shortMonth = new Intl.DateTimeFormat("en-GB", { month: "short" });

/** "2026-06" -> "Jun" */
export function monthAbbrev(period: string): string {
  const [y, m] = period.split("-").map(Number);
  return shortMonth.format(new Date(Date.UTC(y, m - 1, 1)));
}

/**
 * The actual date a monthly bill (dueDay = day-of-month) falls on WITHIN a
 * payday-to-payday cycle. A cycle spans two calendar months, so a bill due on
 * the 28th sits in the FIRST month (right after payday), not the period's month.
 * Returns the earliest matching date in [cycleStart, cycleEnd] (ISO), or null.
 */
export function dueDateInCycle(
  dueDay: number | null | undefined,
  cycleStart: string,
  cycleEnd: string,
): string | null {
  if (dueDay == null) return null;
  const [sy, sm] = cycleStart.split("-").map(Number); // sm is 1-12
  for (let add = 0; add <= 2; add++) {
    const monthIdx = sm - 1 + add;
    const daysInMonth = new Date(Date.UTC(sy, monthIdx + 1, 0)).getUTCDate();
    const day = Math.min(dueDay, daysInMonth); // e.g. "31st" in a 30-day month
    const iso = new Date(Date.UTC(sy, monthIdx, day)).toISOString().slice(0, 10);
    if (iso >= cycleStart && iso <= cycleEnd) return iso;
  }
  return null;
}

/** "2026-09-28" -> "28 Sept" (no leading zero), for a bill's due date. */
export function formatDueDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} ${shortMonth.format(new Date(Date.UTC(y, m - 1, 1)))}`;
}

/** Current month as "YYYY-MM". */
export function currentPeriod(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** Shift a "YYYY-MM" by whole months. */
export function shiftPeriod(period: string, months: number): string {
  const [y, m] = period.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + months, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** Days in the month of a "YYYY-MM". */
export function daysInMonth(period: string): number {
  const [y, m] = period.split("-").map(Number);
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** Whole days from ISO date a (inclusive) to b (exclusive). */
export function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000);
}

/** ISO date one day before the given ISO date. */
export function dayBefore(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d - 1));
  return dt.toISOString().slice(0, 10);
}
